import { lazy, StrictMode, Suspense, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import './index.css'
import { AppShell } from './components/AppShell'
import { HomeRedirect } from './components/auth/HomeRedirect'
import { PageTitleProvider } from './components/PageTitleProvider'
import { MatchList } from './pages/MatchList'
import { RESERVES_BASE, RESERVES_LABEL, RESERVES_TEAM_ID } from './lib/reserves'
import { RaportyTabsLayout } from './components/RaportyTabsLayout'
import { SubTabLayout, type SubTab } from './components/SubTabLayout'
import { initI18n } from './i18n/boot'

// Code-splitting per trasa. Wszystko w jednym imporcie dawało bundle ~960 kB,
// który musiał się ściągnąć w całości ZANIM cokolwiek się narysuje — na słabszym
// łączu przeglądarka potrafiła zawiesić się na `index.js`. Eager zostaje tylko to,
// co widać od razu: powłoka + lista meczów (trasa startowa). Reszta (Motoryka,
// dossier, trening, trasy /print) doczytuje się własnym chunkiem na żądanie.
//
// Strony eksportują nazwane symbole, a React.lazy wymaga `default` — stąd `lazyPage`.
// Trasy /print są bezpieczne: Playwright czeka na `networkidle` + [data-ready="true"],
// więc doczytanie chunku mieści się w oknie oczekiwania (patrz backend/app/pdf.py).
function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType })))
}

const Report = lazyPage(() => import('./pages/Report'), 'Report')
const Print = lazyPage(() => import('./pages/Print'), 'Print')
const ReservesReport = lazyPage(() => import('./pages/ReservesReport'), 'ReservesReport')
const ReservesReportPrint = lazyPage(() => import('./pages/ReservesReport'), 'ReservesReportPrint')
const PeriodSummary = lazyPage(() => import('./pages/PeriodSummary'), 'PeriodSummary')
const PeriodSummaryPrint = lazyPage(() => import('./pages/PeriodSummaryPrint'), 'PeriodSummaryPrint')
const LiveList = lazyPage(() => import('./pages/LiveList'), 'LiveList')
const SetPieceSeason = lazyPage(() => import('./pages/SetPieceSeason'), 'SetPieceSeason')
const LiveSession = lazyPage(() => import('./pages/LiveSession'), 'LiveSession')
const LiveSessionPrint = lazyPage(() => import('./pages/LiveSessionPrint'), 'LiveSessionPrint')
const TeamList = lazyPage(() => import('./pages/TeamList'), 'TeamList')
const LeagueProfile = lazyPage(() => import('./pages/LeagueProfile'), 'LeagueProfile')
const LeagueRanking = lazyPage(() => import('./pages/LeagueProfile'), 'LeagueRanking')
const LeaguePhysicalProfile = lazyPage(() => import('./pages/LeaguePhysicalProfile'), 'LeaguePhysicalProfile')
const TeamDossier = lazyPage(() => import('./pages/TeamDossier'), 'TeamDossier')
const PlayerMapTeamList = lazyPage(() => import('./pages/TeamList'), 'PlayerMapTeamList')
const PlayerMap = lazyPage(() => import('./pages/PlayerMap'), 'PlayerMap')
const PlayerMapPrint = lazyPage(() => import('./pages/PlayerMapPrint'), 'PlayerMapPrint')
const DossierPrint = lazyPage(() => import('./pages/DossierPrint'), 'DossierPrint')
const DossierMotorykaPrint = lazyPage(() => import('./pages/DossierMotorykaPrint'), 'DossierMotorykaPrint')
const Season = lazyPage(() => import('./pages/Season'), 'Season')
const ChanceList = lazyPage(() => import('./pages/ChanceList'), 'ChanceList')
const MatchChances = lazyPage(() => import('./pages/MatchChances'), 'MatchChances')
const PlayerList = lazyPage(() => import('./pages/PlayerList'), 'PlayerList')
const PlayerProfile = lazyPage(() => import('./pages/PlayerProfile'), 'PlayerProfile')
const PlayerCompare = lazyPage(() => import('./pages/PlayerCompare'), 'PlayerCompare')
const Training = lazyPage(() => import('./pages/Training'), 'Training')
const GkTraining = lazyPage(() => import('./pages/GkTraining'), 'GkTraining')
const TrainingReport = lazyPage(() => import('./pages/TrainingReport'), 'TrainingReport')
const TrainingReportPrint = lazyPage(() => import('./pages/TrainingReportPrint'), 'TrainingReportPrint')
const MicrocycleReportPrint = lazyPage(() => import('./pages/MicrocycleReportPrint'), 'MicrocycleReportPrint')
const MotorykaDailyPrint = lazyPage(() => import('./pages/MotorykaDailyPrint'), 'MotorykaDailyPrint')
const MotorykaPlayersPrint = lazyPage(() => import('./pages/MotorykaPlayersPrint'), 'MotorykaPlayersPrint')
const MotorykaComparisonPrint = lazyPage(() => import('./pages/MotorykaComparisonPrint'), 'MotorykaComparisonPrint')
const MotorykaMatchComparePrint = lazyPage(() => import('./pages/MotorykaMatchComparePrint'), 'MotorykaMatchComparePrint')
const MotorykaMatchSummaryPrint = lazyPage(() => import('./pages/MotorykaMatchSummaryPrint'), 'MotorykaMatchSummaryPrint')
const MotorykaRatingsPrint = lazyPage(() => import('./pages/MotorykaRatingsPrint'), 'MotorykaRatingsPrint')
const MotorykaLeaguePrint = lazyPage(() => import('./pages/MotorykaLeaguePrint'), 'MotorykaLeaguePrint')
const Motoryka = lazyPage(() => import('./pages/Motoryka'), 'Motoryka')
const PlayerCardList = lazyPage(() => import('./pages/PlayerCardList'), 'PlayerCardList')
const PlayerCard = lazyPage(() => import('./pages/PlayerCard'), 'PlayerCard')
const PlayerRankings = lazyPage(() => import('./pages/PlayerRankings'), 'PlayerRankings')
const PlayerRankingsPrint = lazyPage(() => import('./pages/PlayerRankingsPrint'), 'PlayerRankingsPrint')
const Procedury = lazyPage(() => import('./pages/Procedury'), 'Procedury')
const Akademia = lazyPage(() => import('./pages/Akademia'), 'Akademia')
const AkademiaPlayer = lazyPage(() => import('./pages/AkademiaPlayer'), 'AkademiaPlayer')
const AkademiaKadra = lazyPage(() => import('./pages/AkademiaKadra'), 'AkademiaKadra')
const Account = lazyPage(() => import('./pages/Account'), 'Account')
const SetPassword = lazyPage(() => import('./pages/SetPassword'), 'SetPassword')
const AdminUsers = lazyPage(() => import('./pages/AdminUsers'), 'AdminUsers')
const SquadPlanner = lazyPage(() => import('./pages/SquadPlanner'), 'SquadPlanner')
const Home = lazyPage(() => import('./pages/Home'), 'Home')
const Calendar = lazyPage(() => import('./pages/Calendar'), 'Calendar')
// Moduł VALD (ForceDecks, NordBord, ForceFrame, DynaMo) — także powłoka modułu (bramka sekcji
// `vald`, nagłówek, pigułki) leniwie: reszta aplikacji nie ściąga jej kodu.
const ValdLayout = lazyPage(() => import('./vald/ValdLayout'), 'ValdLayout')
const ValdStart = lazyPage(() => import('./pages/vald/ValdStart'), 'ValdStart')
const ValdDay = lazyPage(() => import('./pages/vald/ValdDay'), 'ValdDay')
const ValdPlayers = lazyPage(() => import('./pages/vald/ValdPlayers'), 'ValdPlayers')
const ValdJumps = lazyPage(() => import('./pages/vald/ValdJumps'), 'ValdJumps')
const ValdStrength = lazyPage(() => import('./pages/vald/ValdStrength'), 'ValdStrength')
const ValdTests = lazyPage(() => import('./pages/vald/ValdTests'), 'ValdTests')
const ValdInterpretation = lazyPage(() => import('./pages/vald/ValdInterpretation'), 'ValdInterpretation')
const ValdPlayer = lazyPage(() => import('./pages/vald/ValdPlayer'), 'ValdPlayer')
const ValdPlayerReport = lazyPage(() => import('./pages/vald/ValdPlayerReport'), 'ValdPlayerReport')
const ValdPlayerReportPrint = lazyPage(() => import('./pages/vald/ValdPlayerReportPrint'), 'ValdPlayerReportPrint')

// Analiza Rywala — menu zależne od modułu konta (korekta sztabu 26.10.2026):
//   • Sztab: Drużyny · Szanse (szanse to wbijanie sztabu/analityka); porównanie
//     zawodników otwiera się z profilu piłkarza („Porównaj z innymi").
//   • Scout / Dyrektor sportowy — zakładka „Skauting" (28.10.2026): Profil ligi ·
//     Zawodnicy · Porównanie zawodników. Drużyny bez osobnej pigułki — duża tabela
//     ligi zeszła, drużyny otwiera mała tabela w profilu ligi (/dossier zostaje).
// Mapa zawodników (przycisk w nagłówku karty drużyny) i Sezon zeszły z menu
// w obu widokach — trasy zostają, sekcje dostępu też.
const ANALIZA_SUBTABS: SubTab[] = [
  { to: '/liga', label: 'Profil ligi', views: ['scout', 'kadra'] },
  { to: '/dossier', label: 'Drużyny', views: ['sztab'] },
  { to: '/gracze', label: 'Zawodnicy', views: ['scout', 'kadra'] },
  { to: '/gracze/porownanie', label: 'Porównanie zawodników', views: ['scout', 'kadra'] },
  { to: '/szanse', label: 'Szanse', views: ['sztab'] },
]

// Trening — raport jednostki (domyślnie) + wbijanie metryk (tagowanie) + bramkarze
// (konspekt, powtórzenia treningu BR, motoryka bramkarzy — trener BR, 21.10.2026).
const TRENING_SUBTABS: SubTab[] = [
  { to: '/raport-trening', label: 'Raport' },
  { to: '/trening', label: 'Wbijanie metryk' },
  { to: '/bramkarze', label: 'Bramkarze' },
]

// Zawodnicy (Indywidualna Karta Zawodnika) — karty I zespołu + Akademia (lista
// i karty zawodników z Teamworks + AMS, linki do widoków akademii) + rankingi kadry + procedury rozwoju piłkarza (statyczny
// dokument sztabu; URL /procedury zostaje — zakładki tylko go grupują).
const IKZ_SUBTABS: SubTab[] = [
  { to: '/karta-zawodnika', label: 'I Zespół' },
  { to: '/akademia', label: 'Akademia' },
  { to: '/rankingi', label: 'Rankingi' },
  { to: '/procedury', label: 'Procedury' },
]

// Widoczne tylko w oknie doczytywania chunku trasy — celowo bez spinnera i bez
// `data-ready`, żeby nie mignąć w PDF-ie ani nie oszukać czekania Playwrighta.
// Inline (nie osobny komponent), bo main.tsx nic nie eksportuje — react-refresh
// wymaga wtedy, by w pliku nie było definicji komponentów.
const routeFallback = <div className="p-6 text-sm text-ink-400">Wczytywanie…</div>

// Wersja angielska (src/i18n): warstwa słownika startuje przed pierwszym renderem.
void initI18n().then(() => createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      {/* Tytuł karty per trasa (lib/pageTitle.ts) — także /print, bo z niego tytuł PDF-u. */}
      <PageTitleProvider>
      <Suspense fallback={routeFallback}>
        <Routes>
          {/* Print routes — no shell, no chrome, just the renderable surface */}
          <Route path="/raporty/:matchId/print" element={<Print />} />
          <Route path="/raporty/legia-ii/:matchId/print" element={<ReservesReportPrint />} />
          <Route path="/raporty/live/:sessionId/print" element={<LiveSessionPrint />} />
          <Route path="/raporty/podsumowanie/print" element={<PeriodSummaryPrint />} />
          <Route path="/dossier/:teamId/print" element={<DossierPrint />} />
          <Route path="/dossier/:teamId/motoryka/print" element={<DossierMotorykaPrint />} />
          <Route path="/mapa/:teamId/print" element={<PlayerMapPrint />} />
          <Route path="/raport-trening/:unitNumber/print" element={<TrainingReportPrint />} />
          <Route path="/raport-mikrocykl/print" element={<MicrocycleReportPrint />} />
          <Route path="/raport-motoryka-dzienne/print" element={<MotorykaDailyPrint />} />
          <Route path="/raport-motoryka-zawodnicy/print" element={<MotorykaPlayersPrint />} />
          <Route path="/raport-motoryka-porownanie/print" element={<MotorykaComparisonPrint />} />
          <Route path="/raport-motoryka-mecz-porownanie/print" element={<MotorykaMatchComparePrint />} />
          <Route path="/raport-motoryka-pomeczowy/print" element={<MotorykaMatchSummaryPrint />} />
          <Route path="/raport-motoryka-ocena/print" element={<MotorykaRatingsPrint />} />
          <Route path="/raport-motoryka-liga/print" element={<MotorykaLeaguePrint />} />
          <Route path="/rankingi/print" element={<PlayerRankingsPrint />} />
          <Route path="/vald/zawodnicy/:profileId/raport/print" element={<ValdPlayerReportPrint />} />

          {/* All other surfaces sit inside the shell */}
          <Route element={<AppShell />}>
            <Route path="/" element={<HomeRedirect />} />
            {/* Strona startowa po zalogowaniu — każde konto; widżety i linki dobiera
                sama strona wg uprawnień sekcji (lib/homeView.ts). */}
            <Route path="/home" element={<Home />} />
            {/* Raporty meczowe — podzakładki Mecze (StatsBomb) / Podsumowanie okresowe /
                LIVE (sesje tagowania) / SFG. Listy dzielą pasek; szczegóły (/raporty/:id,
                /raporty/live/:id) są poza paskiem — mają własne linki „← wróć".
                /raporty/ranking to stary adres podsumowania (zakładki, linki sztabu). */}
            <Route element={<RaportyTabsLayout />}>
              <Route path="/raporty" element={<MatchList />} />
              <Route path="/raporty/podsumowanie" element={<PeriodSummary />} />
              <Route path="/raporty/ranking" element={<Navigate to="/raporty/podsumowanie" replace />} />
              <Route path="/raporty/live" element={<LiveList />} />
              <Route path="/raporty/sfg" element={<SetPieceSeason />} />
              <Route
                path="/raporty/legia-ii"
                element={<MatchList teamId={RESERVES_TEAM_ID} base={RESERVES_BASE} teamLabel={RESERVES_LABEL} title="Raporty Meczowe · Legia II" />}
              />
            </Route>
            <Route path="/raporty/:matchId" element={<Report />} />
            <Route path="/raporty/legia-ii/:matchId" element={<ReservesReport />} />
            <Route path="/raporty/live/:sessionId" element={<LiveSession />} />

            {/* Analiza Rywala — podzakładki wg modułu konta (ANALIZA_SUBTABS).
                Widoki-listy dzielą pasek (także /mapa i /sezon spoza menu);
                szczegóły (dossier/:id, gracze/:id) są poza paskiem — mają własne
                linki „← wróć". */}
            <Route element={<SubTabLayout tabs={ANALIZA_SUBTABS} />}>
              <Route path="/liga" element={<LeagueProfile />} />
              <Route path="/liga/ranking" element={<LeagueRanking />} />
              <Route path="/liga/motoryka" element={<LeaguePhysicalProfile />} />
              <Route path="/dossier" element={<TeamList />} />
              <Route path="/gracze" element={<PlayerList />} />
              <Route path="/gracze/porownanie" element={<PlayerCompare />} />
              <Route path="/sezon" element={<Season />} />
              <Route path="/szanse" element={<ChanceList />} />
              <Route path="/mapa" element={<PlayerMapTeamList />} />
            </Route>
            <Route path="/dossier/:teamId" element={<TeamDossier />} />
            <Route path="/mapa/:teamId" element={<PlayerMap />} />
            <Route path="/gracze/:playerId" element={<PlayerProfile />} />
            <Route path="/szanse/:matchId" element={<MatchChances />} />

            {/* IKZ — podzakładki I Zespół (lista kart) / Akademia / Rankingi / Procedury;
                widok karty (/karta-zawodnika/:playerId) poza paskiem — ma własny link „← wróć". */}
            <Route element={<SubTabLayout tabs={IKZ_SUBTABS} />}>
              <Route path="/karta-zawodnika" element={<PlayerCardList />} />
              <Route path="/akademia" element={<Akademia />} />
              {/* Parowanie Teamworks ↔ StatsBomb ↔ AMS (migracja 070) — pod pigułką Akademii. */}
              <Route path="/akademia/kadra" element={<AkademiaKadra />} />
              <Route path="/rankingi" element={<PlayerRankings />} />
              <Route path="/procedury" element={<Procedury />} />
            </Route>
            <Route path="/karta-zawodnika/:playerId" element={<PlayerCard />} />
            {/* Karta zawodnika akademii (Teamworks + AMS, sekcja `akademia`) — też poza paskiem. */}
            <Route path="/akademia/:profileId" element={<AkademiaPlayer />} />

            {/* Trening — podzakładki Raport (domyślnie) / Wbijanie metryk / Bramkarze. */}
            <Route element={<SubTabLayout tabs={TRENING_SUBTABS} />}>
              <Route path="/raport-trening" element={<TrainingReport />} />
              <Route path="/trening" element={<Training />} />
              <Route path="/bramkarze" element={<GkTraining />} />
            </Route>

            <Route path="/motoryka" element={<Motoryka />} />

            {/* VALD — badania siły i skoczności, za logowaniem niezależnie od flagi (sekcja
                `vald`; dane o charakterze zdrowotnym). Bramkę, nagłówek modułu i pigułki
                stron daje ValdLayout; podzakładki Skoków i Siły są w samych stronach. */}
            <Route element={<ValdLayout />}>
              <Route path="/vald" element={<ValdStart />} />
              <Route path="/vald/dzien" element={<ValdDay />} />
              <Route path="/vald/skoki" element={<ValdJumps />} />
              <Route path="/vald/skoki/:widok" element={<ValdJumps />} />
              <Route path="/vald/sila" element={<ValdStrength />} />
              <Route path="/vald/sila/:widok" element={<ValdStrength />} />
              <Route path="/vald/interpretacja" element={<ValdInterpretation />} />
              <Route path="/vald/zawodnicy" element={<ValdPlayers />} />
              <Route path="/vald/zawodnicy/:profileId" element={<ValdPlayer />} />
              <Route path="/vald/zawodnicy/:profileId/raport" element={<ValdPlayerReport />} />
              <Route path="/vald/testy" element={<Navigate to="/vald/testy/forcedecks" replace />} />
              <Route path="/vald/testy/:device" element={<ValdTests />} />
            </Route>

            {/* Konta i dostęp — celowo bez zakładki w AppShell, do czasu wpięcia
                logowania w całą aplikację: /konto (własne konto, zmiana hasła)
                i /admin (konta, role, macierz dostępu; tylko admin). */}
            <Route path="/konto" element={<Account />} />
            {/* Hasło z linku z maila (zaproszenie / reset): token po „#" (#t=…). */}
            <Route path="/konto/ustaw-haslo" element={<SetPassword />} />
            <Route path="/admin" element={<AdminUsers />} />
            {/* Kalendarz Legii II i akademii z Teamworks (sekcja `kalendarz`). */}
            <Route path="/kalendarz" element={<Calendar />} />
            {/* Squad planner — ostatnia zakładka belki, za logowaniem niezależnie od flagi
                (sekcje `squad.*`); shadow teamy prywatne per konto. */}
            <Route path="/squad-planner" element={<SquadPlanner />} />
            <Route path="*" element={<HomeRedirect />} />
          </Route>
        </Routes>
      </Suspense>
      </PageTitleProvider>
    </BrowserRouter>
  </StrictMode>,
))
